/**
 * «Сучасна війна»: teams, quarters, walls and flags.
 *
 * The map is a match rather than a world: people arrive into a lobby, put
 * themselves into teams, and the game begins when whoever made it says so.
 * Everything in here is the arithmetic of that - which quarter a team owns,
 * where the walls run, where the flags stand, and what the clock is doing -
 * and none of it has ever seen a socket.
 *
 * That is the whole reason it is a file of its own. The rules are a server's
 * to enforce and a page's to draw, and both have to agree about every number
 * in them: where the wall is, when it comes down, whose flag that is. Two
 * copies of this arithmetic would disagree inside a week.
 *
 * The shape of a match, in the words it was asked for:
 *
 *   - "Той хто створює гру вибирає чекбоксом «проти ботів»" - one team of
 *     people against a team of bots, or teams people make themselves.
 *   - "Максимально 4 команди", and the world is split to match: one wall for
 *     two teams, two crossed walls for three or four.
 *   - "Час на підготовку задає створювач гри, за замовчуванням 10 хвилин."
 *   - "Коли час на підготовку закінчився стіна перетворюється на блок
 *     дерева." The floor of the world stays незламний.
 *   - "Перемагає той хто знищить прапор суперника."
 */

/** Four, and the map is quartered to match. */
export const MAX_TEAMS = 4;

/** Ten minutes to dig in, unless whoever made the match says otherwise. */
export const PREP_SECONDS = 10 * 60;

/** How high the wall stands over the ground. Under it, it goes to the floor. */
export const WALL_HEIGHT = 10;

/**
 * The bot army: how many, how often one is born, and how long it takes them
 * to pull a flag down.
 *
 * An orc has no адамантій and never will - the tier rule is about what a
 * player is holding - so without a rule of their own a team of bots could
 * never win. Standing over the enemy flag for half a minute is that rule,
 * and it is slow on purpose: it should cost the other side a mistake, not a
 * moment's inattention.
 */
export const BOT_ARMY = 24;
export const BOT_EVERY = 6;
export const BOT_CHOP = 30;

/** How far the flag's pole rises out of the ground. */
export const FLAG_HEIGHT = 3;

export type WarPhase =
  /** Nobody is in the world yet: teams are being made and joined. */
  | "lobby"
  /** Everybody is in, the walls are up, and the clock is running down. */
  | "prepare"
  /** The walls are wood and anybody can be anywhere. */
  | "fight"
  /** One flag left standing, and the table of what everybody did. */
  | "over";

export interface WarTeam {
  /** 0 to 3. Decides the quarter, the flag and the spawn. */
  readonly id: number;
  readonly name: string;
  /** A team of bots rather than of people. At most one, and it flies black. */
  readonly bots: boolean;
  /** Names, because that is what a player is called on the wire. */
  readonly members: readonly string[];
  /** Whether their прапор is still standing. */
  readonly alive: boolean;
}

export interface WarScore {
  readonly name: string;
  readonly team: number;
  readonly kills: number;
  readonly deaths: number;
}

/** A rectangle of the map, in blocks: `[from, to)` on both axes. */
export interface Quarter {
  readonly x0: number;
  readonly x1: number;
  readonly z0: number;
  readonly z1: number;
}

/**
 * How many walls a match of this size needs.
 *
 * "Якщо команди дві - стіна одна, якщо команд більше двох то стіни дві вони
 * перпендикулярні та ділять світ на 4 рівні частини." Three teams get the
 * two walls as well: the fourth quarter is simply nobody's, which is a
 * better game than three armies in a world cut into thirds by walls that
 * meet at a point.
 */
export function wallCount(teams: number): number {
  return teams <= 2 ? 1 : 2;
}

/**
 * The part of the map a team owns.
 *
 * With one wall the world is halved along x; with two it is quartered, and
 * the quarters are numbered so that team 0 and team 1 are always across a
 * wall from each other rather than diagonally apart. Two teams that never
 * meet until the last minute is not a match.
 */
export function quarterFor(
  team: number, teams: number, span: number,
): Quarter {
  const half = Math.floor(span / 2);
  if (wallCount(teams) === 1) {
    return team === 0
      ? { x0: 0, x1: half, z0: 0, z1: span }
      : { x0: half, x1: span, z0: 0, z1: span };
  }
  const west = team === 0 || team === 2;
  const north = team === 0 || team === 1;
  return {
    x0: west ? 0 : half,
    x1: west ? half : span,
    z0: north ? 0 : half,
    z1: north ? half : span,
  };
}

/** The middle of a quarter, where the прапор stands. */
export function flagFor(
  team: number, teams: number, span: number,
): { x: number; z: number } {
  const q = quarterFor(team, teams, span);
  return {
    x: Math.floor((q.x0 + q.x1) / 2),
    z: Math.floor((q.z0 + q.z1) / 2),
  };
}

/**
 * Is this column part of a wall?
 *
 * The walls are the middle lines of the map, two blocks thick so that
 * nothing about a diagonal look can see between them.
 */
export function onWall(x: number, z: number, teams: number, span: number): boolean {
  const half = Math.floor(span / 2);
  const acrossX = x === half - 1 || x === half;
  const acrossZ = z === half - 1 || z === half;
  return wallCount(teams) === 1 ? acrossX : acrossX || acrossZ;
}

/**
 * Where somebody on this team wakes up.
 *
 * Beside their own flag rather than on top of it: a team that spawns inside
 * its own прапор spends the first second of the match standing in it.
 */
export function spawnFor(
  team: number, teams: number, span: number, ground: number,
): { x: number; y: number; z: number } {
  const flag = flagFor(team, teams, span);
  return { x: flag.x + 3.5, y: ground + 1, z: flag.z + 3.5 };
}

/**
 * The clock, and what it means.
 *
 * One number and a phase, the way the hill's round works, because both
 * sides of the wire have to be able to say what the state is from what they
 * were last told rather than from what they remember.
 */
export interface WarClock {
  readonly phase: WarPhase;
  /** Seconds left of the preparing, or 0 once it is over. */
  readonly left: number;
}

export function warClockAt(elapsed: number, prep: number): WarClock {
  if (elapsed < prep) return { phase: "prepare", left: prep - elapsed };
  return { phase: "fight", left: 0 };
}

/**
 * Who has won, if anybody has.
 *
 * "Коли всі прапори знищені крім одного" - so a match ends when exactly one
 * team still has a flag, and not when the last flag falls. Nought teams
 * standing is possible only if two flags come down in the same instant, and
 * the answer to that is null: nobody won, and the table still says what
 * everybody did.
 */
export function winnerOf(teams: readonly WarTeam[]): WarTeam | null {
  const standing = teams.filter((t) => t.alive);
  return standing.length === 1 ? standing[0]! : null;
}

export function matchOver(teams: readonly WarTeam[]): boolean {
  return teams.length > 1 && teams.filter((t) => t.alive).length <= 1;
}

/**
 * The table at the end: every player, worst-off team last.
 *
 * "Видно хто переможець, хто скільки раз загинув і хто скільки кого вбив."
 * Sorted by team and then by kills, because the question somebody asks of a
 * scoreboard is "how did we do" before "how did I do".
 */
export function standings(
  scores: readonly WarScore[], teams: readonly WarTeam[],
): readonly WarScore[] {
  const won = winnerOf(teams)?.id ?? -1;
  return [...scores].sort((a, b) => {
    if (a.team !== b.team) {
      if (a.team === won) return -1;
      if (b.team === won) return 1;
      return a.team - b.team;
    }
    if (a.kills !== b.kills) return b.kills - a.kills;
    return a.deaths - b.deaths;
  });
}

/**
 * Скрині з порохом: how far apart they are, and how deep.
 *
 * "Оскільки немає поки де брати порох - нехай він буде захований в землі у
 * скринях, нехай його буде повна скриня." So every quarter of the map holds
 * several, and finding one is a dig rather than a walk: deep enough that a
 * team has to sink a shaft, shallow enough that it is not the same trip as
 * the адамантій at the floor of the world.
 */
export const SUPPLY_CELL = 48;
export const SUPPLY_MIN_DEPTH = 8;
export const SUPPLY_MAX_DEPTH = 26;

/** Where this district's chest is buried, in blocks, and how deep. */
export function supplySpot(
  seed: number, gx: number, gz: number,
): { x: number; z: number; depth: number } {
  const mix = (salt: number): number => {
    let h = (gx * 374761393 + gz * 668265263 + (seed + salt) * 2246822519) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = (h * 1274126177) >>> 0;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
  const span = SUPPLY_MAX_DEPTH - SUPPLY_MIN_DEPTH + 1;
  return {
    // Kept a few blocks inside the district so two chests can never share a
    // column with the wall or with each other.
    x: gx * SUPPLY_CELL + 4 + Math.floor(mix(17) * (SUPPLY_CELL - 8)),
    z: gz * SUPPLY_CELL + 4 + Math.floor(mix(29) * (SUPPLY_CELL - 8)),
    depth: SUPPLY_MIN_DEPTH + Math.floor(mix(41) * span),
  };
}
